import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { SMTConfirmDialogComponent } from 'projects/alden-extension/src/lib/dialogs/confirm/confirm.dialog';
import { takeUntil } from 'rxjs/operators';

export interface BasicDialogComponentProps {
    controlClose?: boolean;
    formName?: string; 
    title: string;
    componentRef: any;
    componentData?: { [id: string]: any };
    noLabel: string;
    yesLabel: string;
    hideYesButton?: boolean;
}

@Component({
  templateUrl: './basic.dialog.html',
  styleUrls: ['./basic.dialog.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BasicDialog implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('viewContainerRef', { read: ViewContainerRef, static: false }) viewContainerRef!: ViewContainerRef;
  
  componentInstance: any;
  onDestroy$: Subject<boolean> = new Subject<boolean>();
  
  constructor(
    private dialogRef: MatDialogRef<BasicDialog>,
    private dialog: MatDialog,
    private cdRef: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data:BasicDialogComponentProps
  ) {
    this.controlClose = this.controlClose == undefined ? true : this.controlClose;
  }

  ngOnInit(): void {

    if(this.controlClose) {
      // Interceptamos el evento de cerrar el diálogo
      this.dialogRef.backdropClick().subscribe(() => {
        this.closeDialog();
      });
    
      // Interceptamos el evento de presionar la tecla Esc
      this.dialogRef.keydownEvents().subscribe(event => {
        if (event.key === "Escape") {
          this.closeDialog();
        }
      });
    }
  }

  ngAfterViewInit(): void {
    //Cargamos componente interno
    const componentRef = this.componentRef;
    if(!componentRef)
      return;
    
    this.componentInstance = this.viewContainerRef?.createComponent(componentRef)?.instance;

    const componentData = this.componentData;
    Object.entries(componentData).forEach(([key, value]) => {
      this.componentInstance[key] = value;
    });

    this.componentInstance.controlClose?.pipe(takeUntil(this.onDestroy$)).subscribe((controlClose: boolean) => {
      this.controlClose = controlClose;
    });

    this.componentInstance.formName?.pipe(takeUntil(this.onDestroy$)).subscribe((formName: string) => {
      this.formName = formName;
    });

    this.componentInstance.title?.pipe(takeUntil(this.onDestroy$)).subscribe((title: string) => {
      this.title = title;
    });

    this.componentInstance.noLabel?.pipe(takeUntil(this.onDestroy$)).subscribe((noLabel: string) => {
      this.noLabel = noLabel;
    });

    this.componentInstance.yesLabel?.pipe(takeUntil(this.onDestroy$)).subscribe((yesLabel: string) => {
      this.yesLabel = yesLabel;
    });

    this.componentInstance.hideYesButton?.pipe(takeUntil(this.onDestroy$)).subscribe((hideYesButton: boolean) => {
      this.hideYesButton = hideYesButton;
    });

    this.componentInstance.closeDialog?.pipe(takeUntil(this.onDestroy$)).subscribe((force?: boolean) => {
      this.closeDialog(force);
    });

    this.cdRef.detectChanges();
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }
  
  closeDialog(force?: boolean){
    if(this.controlClose && !force){
      const confirmDialogRef = this.dialog.open(SMTConfirmDialogComponent, {
        data: {
          title: 'ALDEN.DIALOGS.BASIC.CLOSE_DIALOG.TITLE',
          message: 'ALDEN.DIALOGS.BASIC.CLOSE_DIALOG.MESSAGE',
          noLabel: 'ALDEN.DIALOGS.BASIC.CLOSE_DIALOG.NO_LABEL',
          yesLabel: 'ALDEN.DIALOGS.BASIC.CLOSE_DIALOG.YES_LABEL'
        },
        minWidth: '250px'
      });

      confirmDialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.dialogRef.close();
        }
      });
    }else {
      this.dialogRef.close();
    }
  }

  onSubmit() {
    this.componentInstance?.onSubmit();
  }

  get controlClose(): boolean {
    const controlClose = this.data.controlClose;
    return controlClose == undefined ? true : controlClose;
  }
  get formName(): string {
    return this.data.formName || 'form';
  }
  get title(): string {
    return this.data.title;
  }
  get componentRef(): any {
    return this.data.componentRef;
  }
  get componentData(): { [id: string]: any } {
    return this.data.componentData;
  }
  get noLabel(): string {
    return this.data.noLabel || 'ALDEN.DIALOGS.BASIC.NO_LABEL';
  }
  get yesLabel(): string {
    return this.data.yesLabel || 'ALDEN.DIALOGS.BASIC.YES_LABEL';
  }
  get hideYesButton(): boolean {
    return this.data.hideYesButton;
  }

  set controlClose(value: boolean) {
    this.data.controlClose = value;
  }
  set formName(value: string) {
    this.data.formName = value;
  }
  set title(value: string) {
    this.data.title = value;
  }
  set componentRef(value: any) {
    this.data.componentRef = value;
  }
  set componentData(value: { [id: string]: any }) {
    this.data.componentData = value;
  }
  set noLabel(value: string) {
    this.data.noLabel = value;
  }
  set yesLabel(value: string) {
    this.data.yesLabel = value;
  }
  set hideYesButton(value: boolean) {
    this.data.hideYesButton = value;
  }

}
